import {
  offerCardPrimaryAction,
  offerDecisionState,
} from './offerStatusActions';

export const RENT_PROGRESS_PHASES = Object.freeze({
  NEGOTIATION: 'negotiation',
  DOCUMENTATION: 'documentation',
  CONTRACT: 'contract',
});

export const RENT_PROGRESS_LIMIT_DAYS = 30;

export const RENT_PROGRESS_PHASE_TABS = Object.freeze([
  { value: RENT_PROGRESS_PHASES.NEGOTIATION, label: 'Negociação' },
  { value: RENT_PROGRESS_PHASES.DOCUMENTATION, label: 'Documentação' },
  { value: RENT_PROGRESS_PHASES.CONTRACT, label: 'Contrato' },
]);

export const RENT_PROGRESS_CONTRACT_PERIODS = Object.freeze([
  { value: 'TODAY', label: 'hoje', daysAgo: 0 },
  { value: 'LAST_10_DAYS', label: 'últimos 10 dias', daysAgo: 10 },
  { value: 'LAST_30_DAYS', label: 'últimos 30 dias', daysAgo: 30 },
]);

export const RENT_PROGRESS_DOCUMENTATION_PERIODS = Object.freeze([
  { value: 'TODAY', label: 'hoje', daysAgo: 0 },
  { value: 'LAST_10_DAYS', label: 'últimos 10 dias', daysAgo: 10 },
  { value: 'LAST_30_DAYS', label: 'últimos 30 dias', daysAgo: 30 },
]);

export const RENT_PROGRESS_CONTRACT_STATUSES = Object.freeze([
  { value: 'ALL', label: 'todos os contratos' },
  { value: 'SIGNED', label: 'assinados' },
  { value: 'CANCELLED', label: 'cancelados' },
  { value: 'DRAFT', label: 'em fase de minuta' },
  { value: 'WAITING_SIGNATURE', label: 'em fase de pré-assinaturas' },
]);

export const RENT_PROGRESS_LOAD_MORE_LABELS = Object.freeze({
  [RENT_PROGRESS_PHASES.NEGOTIATION]: 'Mais negociações',
  [RENT_PROGRESS_PHASES.DOCUMENTATION]: 'Mais documentações',
  [RENT_PROGRESS_PHASES.CONTRACT]: 'Mais contratos',
});

const PHASE_EMPTY_COPY = Object.freeze({
  [RENT_PROGRESS_PHASES.NEGOTIATION]: {
    title: limitDays =>
      `Não há visitas com propostas em negociação nos últimos ${limitDays} dias`,
    description:
      'Aqui você acompanha o andamento das propostas em negociação das visitas que realizou.',
    filterDescription:
      'Altere os filtros aplicados para buscar suas propostas em negociação.',
  },
  [RENT_PROGRESS_PHASES.DOCUMENTATION]: {
    title: limitDays =>
      `Não há visitas com propostas em documentação nos últimos ${limitDays} dias`,
    description:
      'Aqui você acompanha o andamento das propostas em documentação das visitas que realizou.',
    filterDescription:
      'Altere os filtros aplicados para buscar suas propostas em documentação.',
  },
  [RENT_PROGRESS_PHASES.CONTRACT]: {
    title: limitDays => `Não há contratos nos últimos ${limitDays} dias`,
    description:
      'Aqui você acompanha os contratos assinados ou cancelados das visitas que realizou.',
    filterDescription:
      'Altere os filtros aplicados para buscar suas propostas em fase de contrato.',
  },
});

const normalized = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizedToken = value =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

const isPresent = value =>
  value !== undefined && value !== null && value !== '';

const asArray = value => (Array.isArray(value) ? value : []);

const asPanelItems = value => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  if (
    [
      value.status,
      value.key,
      value.kind,
      value.name,
      value.title,
      value.document_type,
      value.documentType,
    ].some(isPresent)
  ) {
    return [value];
  }

  return [];
};

const firstPresent = values => values.find(isPresent);

export const normalizeRentProgressPhase = value => {
  const phase = normalized(value);
  if (
    ['documentacao', 'documentação', 'docs', 'documentation'].includes(phase)
  ) {
    return RENT_PROGRESS_PHASES.DOCUMENTATION;
  }
  if (['contrato', 'contratos', 'contract', 'contracts'].includes(phase)) {
    return RENT_PROGRESS_PHASES.CONTRACT;
  }
  return RENT_PROGRESS_PHASES.NEGOTIATION;
};

export const normalizeRentProgressContractPeriod = value => {
  const period = String(value || '')
    .trim()
    .toUpperCase();
  return RENT_PROGRESS_CONTRACT_PERIODS.some(item => item.value === period)
    ? period
    : 'LAST_30_DAYS';
};

export const normalizeRentProgressContractStatus = value => {
  const status = String(value || '')
    .trim()
    .toUpperCase();
  return RENT_PROGRESS_CONTRACT_STATUSES.some(item => item.value === status)
    ? status
    : 'ALL';
};

export const normalizeRentProgressDocumentationPeriod = value => {
  const period = String(value || '')
    .trim()
    .toUpperCase();
  return RENT_PROGRESS_DOCUMENTATION_PERIODS.some(item => item.value === period)
    ? period
    : 'LAST_30_DAYS';
};

export const rentProgressContractPeriodOption = value =>
  RENT_PROGRESS_CONTRACT_PERIODS.find(
    item => item.value === normalizeRentProgressContractPeriod(value)
  );

export const rentProgressDocumentationPeriodOption = value =>
  RENT_PROGRESS_DOCUMENTATION_PERIODS.find(
    item => item.value === normalizeRentProgressDocumentationPeriod(value)
  );

export const rentProgressContractStatusOption = value =>
  RENT_PROGRESS_CONTRACT_STATUSES.find(
    item => item.value === normalizeRentProgressContractStatus(value)
  );

export const rentProgressDocumentationFilterLabel = ({ period } = {}) =>
  rentProgressDocumentationPeriodOption(period).label;

export const rentProgressContractFilterLabel = ({ period, status } = {}) => {
  const periodLabel = rentProgressContractPeriodOption(period).label;
  const statusLabel = rentProgressContractStatusOption(status).label;
  return `${statusLabel} · ${periodLabel}`;
};

const contractForOffer = offer => {
  if (offer?.ccv_contract) return offer.ccv_contract;
  return asArray(offer?.property_contracts).find(contract =>
    ['ccv', 'rental_contract', 'rental', 'rent', 'contract'].includes(
      normalized(contract?.kind)
    )
  );
};

const rentalOfferStatus = offer =>
  offer?.metadata?.rental_offer_status ||
  offer?.metadata?.rentalOfferStatus ||
  offer?.rental_offer_status ||
  offer?.rentalOfferStatus;

export const documentationStatusForOffer = offer =>
  offer?.documentation_status || offer?.kyc_status || null;

const kycForOffer = offer =>
  offer?.kyc_verification ||
  asArray(offer?.kyc_verifications)[0] ||
  documentationStatusForOffer(offer);

const hasNativeDocumentationStatus = offer =>
  Boolean(documentationStatusForOffer(offer));

const tokenSources = offer =>
  [
    offer?.status,
    offer?.status_v3,
    offer?.status_key,
    offer?.macro_status,
    offer?.macroStatus,
    offer?.phase,
    offer?.summary?.status,
    offer?.summary?.macro_status,
    offer?.timeline_payload?.macro_status_label,
    offer?.metadata?.status,
    offer?.metadata?.phase,
  ]
    .filter(isPresent)
    .map(normalized)
    .join(' ');

export const hasRentProgressContractPhase = offer => {
  const tokens = tokenSources(offer);
  return Boolean(
    contractForOffer(offer) ||
      offer?.metadata?.contract_deadline_date ||
      offer?.metadata?.contract_deadline_at ||
      offer?.metadata?.contract_start_date ||
      offer?.metadata?.contract_start_at ||
      tokens.includes('contract') ||
      tokens.includes('contrato') ||
      tokens.includes('ccv') ||
      tokens.includes('signature') ||
      tokens.includes('assinatura')
  );
};

export const hasRentProgressDocumentationPhase = offer => {
  const tokens = tokenSources(offer);
  return Boolean(
    hasNativeDocumentationStatus(offer) ||
      kycForOffer(offer) ||
      offer?.status === 'accepted' ||
      tokens.includes('accepted') ||
      tokens.includes('aceita') ||
      tokens.includes('documentation') ||
      tokens.includes('documentacao') ||
      tokens.includes('documentação') ||
      tokens.includes('docs') ||
      tokens.includes('kyc') ||
      tokens.includes('credit') ||
      tokens.includes('credito') ||
      tokens.includes('crédito') ||
      tokens.includes('guarantee') ||
      tokens.includes('garantia')
  );
};

export const rentProgressPhaseForOffer = offer => {
  if (hasRentProgressContractPhase(offer)) {
    return RENT_PROGRESS_PHASES.CONTRACT;
  }
  if (hasRentProgressDocumentationPhase(offer)) {
    return RENT_PROGRESS_PHASES.DOCUMENTATION;
  }
  return RENT_PROGRESS_PHASES.NEGOTIATION;
};

export const filterOffersByRentProgressPhase = (offers, phase) => {
  const normalizedPhase = normalizeRentProgressPhase(phase);
  return asArray(offers).filter(
    offer => rentProgressPhaseForOffer(offer) === normalizedPhase
  );
};

export const rentProgressContractStatusForOffer = offer => {
  const contract = contractForOffer(offer);
  const status = normalized(
    contract?.status ||
      contract?.quintoandar_status ||
      offer?.metadata?.contract_status ||
      offer?.metadata?.contractStatus
  );

  if (['signed', 'ativo', 'active'].includes(status)) return 'SIGNED';
  if (['canceled', 'cancelled', 'cancelado', 'failed'].includes(status)) {
    return 'CANCELLED';
  }
  if (['pending', 'draft', 'minuta'].includes(status)) return 'DRAFT';
  if (
    [
      'sent',
      'partially_signed',
      'waiting_signature',
      'waiting_signatures',
      'pré-assinaturas',
      'preassinaturas',
      'pre_assinaturas',
      'préassinaturas',
    ].includes(status)
  ) {
    return 'WAITING_SIGNATURE';
  }

  return 'ALL';
};

export const offerMatchesRentProgressContractStatus = (offer, status) => {
  const normalizedStatus = normalizeRentProgressContractStatus(status);
  return (
    normalizedStatus === 'ALL' ||
    rentProgressContractStatusForOffer(offer) === normalizedStatus
  );
};

export const filterOffersByRentProgressContractFilters = (
  offers,
  { status } = {}
) =>
  asArray(offers).filter(offer =>
    offerMatchesRentProgressContractStatus(offer, status)
  );

const plural = (count, singular, pluralLabel) =>
  `${count} ${count === 1 ? singular : pluralLabel}`;

export const rentProgressPhaseHeader = ({ phase, count = 0 } = {}) => {
  const normalizedPhase = normalizeRentProgressPhase(phase);
  if (normalizedPhase === RENT_PROGRESS_PHASES.DOCUMENTATION) {
    if (count === 0) return 'Nenhuma proposta em documentação';
    return plural(
      count,
      'proposta em documentação',
      'propostas em documentação'
    );
  }
  if (normalizedPhase === RENT_PROGRESS_PHASES.CONTRACT) {
    if (count === 0) return 'Nenhuma proposta em fase de contrato';
    return plural(
      count,
      'proposta em fase de contrato',
      'propostas em fase de contrato'
    );
  }
  if (count === 0) return 'Nenhuma proposta em negociação';
  return plural(count, 'proposta em negociação', 'propostas em negociação');
};

export const rentProgressPhaseSupportText = ({
  phase,
  limitDays = RENT_PROGRESS_LIMIT_DAYS,
  period = 'LAST_30_DAYS',
  documentationPeriod = 'LAST_30_DAYS',
} = {}) => {
  const normalizedPhase = normalizeRentProgressPhase(phase);
  if (normalizedPhase === RENT_PROGRESS_PHASES.DOCUMENTATION) {
    const option = rentProgressDocumentationPeriodOption(documentationPeriod);
    if (option.daysAgo === 0) {
      return 'Iniciadas hoje.';
    }
    if (option.daysAgo === 1) {
      return 'Iniciadas ontem.';
    }
    return `Iniciadas nos últimos ${option.daysAgo || limitDays} dias.`;
  }

  if (normalizedPhase === RENT_PROGRESS_PHASES.CONTRACT) {
    const option = rentProgressContractPeriodOption(period);
    if (option.daysAgo === 0) {
      return 'Acompanhe os contratos que foram assinados hoje.';
    }
    if (option.daysAgo === 1) {
      return 'Acompanhe os contratos que foram assinados ontem.';
    }
    return `Acompanhe os contratos que foram assinados nos últimos ${option.daysAgo || limitDays} dias.`;
  }

  return `Iniciadas nos últimos ${limitDays} dias.`;
};

export const rentProgressPhaseLoadMoreLabel = phase =>
  RENT_PROGRESS_LOAD_MORE_LABELS[normalizeRentProgressPhase(phase)];

const rentProgressStepAliases = Object.freeze({
  waitingownerresponse: 'waitingOwnerResponse',
  waiting_owner_response: 'waitingOwnerResponse',
  waitingtenantrespondcounteroffer: 'waitingTenantRespondCounterOffer',
  waiting_tenant_respond_counter_offer: 'waitingTenantRespondCounterOffer',
  waitingownerrespondcounteroffer: 'waitingOwnerRespondCounterOffer',
  waiting_owner_respond_counter_offer: 'waitingOwnerRespondCounterOffer',
  tenantdidntsentoffer: 'tenantDidntSentOffer',
  tenant_didnt_sent_offer: 'tenantDidntSentOffer',
  ownerrejectedoffer: 'ownerRejectedOffer',
  owner_rejected_offer: 'ownerRejectedOffer',
  tenantrejectedoffer: 'tenantRejectedOffer',
  tenant_rejected_offer: 'tenantRejectedOffer',
  stalledrejection: 'stalledRejection',
  stalled_rejection: 'stalledRejection',
  houseinactive: 'houseInactive',
  house_inactive: 'houseInactive',
  houseunpublished: 'houseUnpublished',
  house_unpublished: 'houseUnpublished',
});

const normalizeRentProgressStepKey = value => {
  const normalizedKey = String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();
  return rentProgressStepAliases[normalizedKey] || value || '';
};

const currentRentProgressStep = offer =>
  offer?.current_step ||
  offer?.currentStep ||
  offer?.step ||
  offer?.timeline_payload?.current_step ||
  offer?.timelinePayload?.currentStep ||
  asArray(offer?.timeline).find(step => step?.current) ||
  asArray(offer?.timeline_payload?.steps).find(step => step?.current) ||
  {};

const stepDescription = step =>
  step?.description || step?.status || step?.label || '';

const summaryRow = ({ role, text, status }) => ({
  key: `${role}-${status}-${text}`,
  role,
  text,
  status,
  icon:
    {
      completed: 'i-lucide-circle-check',
      waiting: 'i-lucide-clock-3',
      terminated: 'i-lucide-circle-x',
    }[status] || 'i-lucide-info',
});

const rejectionSummary = step => {
  const reason = stepDescription(step);
  return reason ? `Proposta rejeitada por: "${reason}"` : 'Proposta rejeitada';
};

export const rentProgressNegotiationSummaries = offer => {
  const step = currentRentProgressStep(offer);
  const key = normalizeRentProgressStepKey(step?.key);
  const description = stepDescription(step);

  if (key === 'waitingOwnerResponse') {
    return [
      summaryRow({
        role: 'Interessado',
        text: 'Interessado enviou a proposta',
        status: 'completed',
      }),
      summaryRow({
        role: 'Proprietário',
        text: description || 'Proprietário precisa responder a proposta',
        status: 'waiting',
      }),
    ];
  }

  if (key === 'waitingTenantRespondCounterOffer') {
    return [
      summaryRow({
        role: 'Proprietário',
        text: 'Proprietário enviou uma contra-proposta',
        status: 'completed',
      }),
      summaryRow({
        role: 'Interessado',
        text: description || 'Interessado precisa responder a contra-proposta',
        status: 'waiting',
      }),
    ];
  }

  if (key === 'waitingOwnerRespondCounterOffer') {
    return [
      summaryRow({
        role: 'Interessado',
        text: 'Interessado enviou uma contra-proposta',
        status: 'completed',
      }),
      summaryRow({
        role: 'Proprietário',
        text: description || 'Proprietário precisa responder a contra-proposta',
        status: 'waiting',
      }),
    ];
  }

  if (key === 'tenantDidntSentOffer') {
    return [
      summaryRow({
        role: 'Interessado',
        text:
          description ||
          'Interessado deu início a proposta mas falta finalizar o envio',
        status: 'waiting',
      }),
    ];
  }

  if (key === 'ownerRejectedOffer') {
    return [
      summaryRow({
        role: 'Proprietário',
        text: rejectionSummary(step),
        status: 'terminated',
      }),
    ];
  }

  if (key === 'tenantRejectedOffer') {
    return [
      summaryRow({
        role: 'Interessado',
        text: rejectionSummary(step),
        status: 'terminated',
      }),
    ];
  }

  if (['stalledRejection', 'houseInactive', 'houseUnpublished'].includes(key)) {
    return [
      summaryRow({
        role: 'Negociação',
        text: description || 'Proposta cancelada',
        status: 'terminated',
      }),
    ];
  }

  return [];
};

export const RENT_PROGRESS_PANEL_TABS = Object.freeze([
  {
    key: 'offers',
    label: 'Ofertas',
    icon: 'i-lucide-handshake',
    emptyLabel: 'Nenhuma oferta ativa por enquanto.',
  },
  {
    key: 'documentation',
    label: 'Documentação',
    icon: 'i-lucide-folder-check',
    emptyLabel: 'Nenhuma documentação vinculada por enquanto.',
  },
  {
    key: 'contracts',
    label: 'Contratos',
    icon: 'i-lucide-file-signature',
    emptyLabel: 'Nenhum contrato vinculado por enquanto.',
  },
  {
    key: 'kyc',
    label: 'KYC',
    icon: 'i-lucide-badge-check',
    emptyLabel: 'Nenhum KYC vinculado por enquanto.',
  },
]);

const PANEL_TAB_KEYS = new Set(RENT_PROGRESS_PANEL_TABS.map(tab => tab.key));

const panelDateFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const formatPanelDeadline = value => {
  if (!isPresent(value)) return 'Sem prazo definido';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return panelDateFormatter.format(parsed);
};

const formatPanelCurrency = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
};

const statusConfig = ({
  label,
  tone = 'neutral',
  ctaLabel = 'Abrir etapa',
  ctaKey = 'open_step',
  icon = 'i-lucide-chevron-right',
  blocksNextStep = false,
  completesStep = false,
} = {}) => ({
  label,
  tone,
  cta: {
    key: ctaKey,
    label: ctaLabel,
    icon,
  },
  blocksNextStep,
  completesStep,
});

const panelStage = ({
  key,
  title,
  responsible,
  deadline,
  status,
  cta,
  tone,
  blocksNextStep = false,
  completesStep = false,
}) => ({
  key,
  title,
  responsible: responsible || 'Lais',
  deadline: formatPanelDeadline(deadline),
  statusLabel: status || 'Em acompanhamento',
  statusTone: tone || 'neutral',
  cta: cta || {
    key: 'open_step',
    label: 'Abrir etapa',
    icon: 'i-lucide-chevron-right',
  },
  blocksNextStep,
  completesStep,
});

const stageFromStatus = ({ key, title, responsible, deadline, status }) =>
  panelStage({
    key,
    title,
    responsible,
    deadline,
    status: status.label,
    cta: status.cta,
    tone: status.tone,
    blocksNextStep: status.blocksNextStep,
    completesStep: status.completesStep,
  });

const deadlineFromStep = step =>
  firstPresent([
    step?.deadline_at,
    step?.deadlineAt,
    step?.deadline,
    step?.expires_at,
    step?.expiresAt,
    step?.due_at,
    step?.dueAt,
  ]);

const offerDeadlineValue = offer => {
  const step = currentRentProgressStep(offer);
  return firstPresent([
    deadlineFromStep(step),
    offer?.current_revision?.expires_at,
    offer?.currentRevision?.expiresAt,
    offer?.response_deadline_at,
    offer?.responseDeadlineAt,
    offer?.offer_deadline_at,
    offer?.offerDeadlineAt,
    offer?.metadata?.response_deadline_at,
    offer?.metadata?.offer_deadline_at,
  ]);
};

const documentDeadlineValue = item =>
  firstPresent([
    item?.deadline_at,
    item?.deadlineAt,
    item?.due_at,
    item?.dueAt,
    item?.expires_at,
    item?.expiresAt,
  ]);

const contractDeadlineValue = (offer, contract = null) =>
  firstPresent([
    contract?.deadline_at,
    contract?.deadlineAt,
    contract?.signature_deadline_at,
    contract?.signatureDeadlineAt,
    contract?.expires_at,
    contract?.expiresAt,
    offer?.metadata?.contract_deadline_at,
    offer?.metadata?.contract_deadline_date,
    offer?.metadata?.signature_deadline_at,
  ]);

const panelTitleForOffer = offer =>
  offer?.imovel_titulo ||
  offer?.imovel?.title ||
  offer?.imovel?.name ||
  'Imóvel não informado';

const panelSubtitleForOffer = offer =>
  offer?.contact_name ||
  offer?.contact_email ||
  offer?.contact_phone_number ||
  offer?.contact?.name ||
  'Cliente não vinculado';

const panelReferenceForOffer = offer =>
  firstPresent([
    offer?.imovel?.external_id,
    offer?.imovel?.externalId,
    offer?.imovel?.short_id,
    offer?.imovel?.shortId,
    offer?.imovel_id,
    offer?.house_id,
    offer?.houseId,
  ]) || '-';

const roleForNegotiationStep = (step, decisionState) => {
  const key = normalizeRentProgressStepKey(step?.key);
  if (
    [
      'waitingTenantRespondCounterOffer',
      'tenantDidntSentOffer',
      'tenantRejectedOffer',
    ].includes(key)
  ) {
    return 'Interessado';
  }
  if (
    [
      'waitingOwnerResponse',
      'waitingOwnerRespondCounterOffer',
      'ownerRejectedOffer',
    ].includes(key)
  ) {
    return 'Proprietário';
  }
  if (decisionState.accepted || decisionState.final) return 'Lais';
  return 'Corretor';
};

const negotiationStatusForOffer = (offer, decisionState) => {
  const step = currentRentProgressStep(offer);
  const key = normalizeRentProgressStepKey(step?.key);

  if (key === 'waitingTenantRespondCounterOffer') {
    return statusConfig({
      label: 'Contraproposta aguardando interessado',
      tone: 'warning',
      ctaLabel: 'Responder contraproposta',
      ctaKey: 'counter',
      icon: 'i-lucide-message-square-reply',
      blocksNextStep: true,
    });
  }

  if (key === 'waitingOwnerRespondCounterOffer') {
    return statusConfig({
      label: 'Contraproposta aguardando proprietário',
      tone: 'warning',
      ctaLabel: 'Acompanhar contraproposta',
      ctaKey: 'open_step',
      icon: 'i-lucide-message-circle',
      blocksNextStep: true,
    });
  }

  if (key === 'waitingOwnerResponse') {
    return statusConfig({
      label: 'Proposta aguardando proprietário',
      tone: 'warning',
      ctaLabel: 'Responder proposta',
      ctaKey: 'open_step',
      blocksNextStep: true,
    });
  }

  if (decisionState.accepted) {
    return statusConfig({
      label: 'Oferta aceita',
      tone: 'success',
      ctaLabel: 'Abrir documentação',
      ctaKey: 'kyc',
      icon: 'i-lucide-folder-check',
      completesStep: true,
    });
  }

  if (decisionState.final) {
    return statusConfig({
      label: 'Fluxo concluído',
      tone: 'success',
      ctaLabel: 'Ver contrato assinado',
      ctaKey: 'contract',
      icon: 'i-lucide-file-check-2',
      completesStep: true,
    });
  }

  if (decisionState.expired) {
    return statusConfig({
      label: 'Oferta expirada',
      tone: 'danger',
      ctaLabel: 'Revisar proposta',
      ctaKey: 'open_step',
      icon: 'i-lucide-clock-alert',
      blocksNextStep: true,
    });
  }

  if (decisionState.terminal) {
    return statusConfig({
      label: 'Oferta encerrada',
      tone: 'danger',
      ctaLabel: 'Ver motivo',
      ctaKey: 'open_step',
      icon: 'i-lucide-circle-x',
      blocksNextStep: true,
    });
  }

  const primaryAction = offerCardPrimaryAction(offer, 'negotiation');
  return statusConfig({
    label: primaryAction.label,
    tone: decisionState.hasMutableAction ? 'warning' : 'neutral',
    ctaLabel: primaryAction.label,
    ctaKey: primaryAction.key,
    icon: primaryAction.icon,
    blocksNextStep: !decisionState.hasMutableAction,
  });
};

const documentStatusDetails = value => {
  const token = normalizedToken(value?.status || value?.key || value);

  if (
    [
      'approved',
      'complete',
      'completed',
      'accepted',
      'valid',
      'docs_approved',
      'docsapproved',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Aprovado',
      tone: 'success',
      ctaLabel: 'Ver documento',
      ctaKey: 'document',
      icon: 'i-lucide-file-check-2',
      completesStep: true,
    });
  }

  if (
    [
      'rejected',
      'refused',
      'invalid',
      'denied',
      'docs_demand_resend',
      'docs_resend',
      'resend',
      'needs_resend',
      'need_resend',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Rejeitado',
      tone: 'danger',
      ctaLabel: 'Reenviar documento',
      ctaKey: 'resend_document',
      icon: 'i-lucide-upload',
      blocksNextStep: true,
    });
  }

  if (
    [
      'analysis',
      'in_analysis',
      'in_review',
      'processing',
      'docs_quintoandar_analysis',
      'docs_analysis',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Em análise',
      tone: 'info',
      ctaLabel: 'Acompanhar análise',
      ctaKey: 'document',
      icon: 'i-lucide-search-check',
      blocksNextStep: true,
    });
  }

  return statusConfig({
    label: 'Pendente',
    tone: 'warning',
    ctaLabel: 'Enviar documento',
    ctaKey: 'send_document',
    icon: 'i-lucide-upload',
    blocksNextStep: true,
  });
};

const kycStatusDetails = value => {
  const token = normalizedToken(value?.status || value?.key || value);

  if (
    ['approved', 'verified', 'completed', 'complete', 'valid'].includes(token)
  ) {
    return statusConfig({
      label: 'KYC aprovado',
      tone: 'success',
      ctaLabel: 'Ver KYC',
      ctaKey: 'kyc',
      icon: 'i-lucide-badge-check',
      completesStep: true,
    });
  }

  if (['rejected', 'refused', 'denied', 'invalid', 'error'].includes(token)) {
    return statusConfig({
      label: token === 'error' ? 'Erro no KYC' : 'KYC rejeitado',
      tone: 'danger',
      ctaLabel: 'Reenviar KYC',
      ctaKey: 'submit_kyc',
      icon: 'i-lucide-refresh-cw',
      blocksNextStep: true,
    });
  }

  if (
    [
      'processing',
      'analysis',
      'in_analysis',
      'in_review',
      'pending_review',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'KYC em análise',
      tone: 'info',
      ctaLabel: 'Sincronizar KYC',
      ctaKey: 'sync_kyc',
      icon: 'i-lucide-refresh-cw',
      blocksNextStep: true,
    });
  }

  return statusConfig({
    label: 'KYC pendente',
    tone: 'warning',
    ctaLabel: 'Enviar KYC',
    ctaKey: 'submit_kyc',
    icon: 'i-lucide-shield-check',
    blocksNextStep: true,
  });
};

const contractStatusDetails = (offer, contract) => {
  const token = normalizedToken(
    firstPresent([
      contract?.status,
      contract?.quintoandar_status,
      offer?.metadata?.contract_status,
      offer?.metadata?.contractStatus,
      rentalOfferStatus(offer),
      offer?.status,
    ])
  );

  if (
    [
      'signed',
      'active',
      'contract_signed',
      'contract_start_date_reached',
      'contract_finished',
      'finished',
      'completed',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Contrato assinado',
      tone: 'success',
      ctaLabel: 'Ver contrato assinado',
      ctaKey: 'contract',
      icon: 'i-lucide-file-check-2',
      completesStep: true,
    });
  }

  if (
    [
      'failed',
      'error',
      'canceled',
      'cancelled',
      'contract_failed',
      'contract_cancelled',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Contrato com pendência',
      tone: 'danger',
      ctaLabel: 'Sincronizar contrato',
      ctaKey: 'sync_contract',
      icon: 'i-lucide-refresh-cw',
      blocksNextStep: true,
    });
  }

  if (
    [
      'sent',
      'partially_signed',
      'waiting_signature',
      'waiting_signatures',
      'contract_all_signatures_pending',
      'contract_supply_signature_pending',
      'contract_demand_signature_pending',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Assinaturas pendentes',
      tone: 'warning',
      ctaLabel: 'Abrir assinaturas',
      ctaKey: 'contract',
      icon: 'i-lucide-file-signature',
      blocksNextStep: true,
    });
  }

  if (
    [
      'draft',
      'pending',
      'minuta',
      'contract_quintoandar_in_creation',
      'in_creation',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Minuta em criação',
      tone: 'info',
      ctaLabel: 'Gerar contrato',
      ctaKey: 'generate_contract',
      icon: 'i-lucide-file-plus-2',
      blocksNextStep: true,
    });
  }

  return statusConfig({
    label: contract ? 'Contrato em acompanhamento' : 'Contrato a gerar',
    tone: contract ? 'neutral' : 'warning',
    ctaLabel: contract ? 'Abrir contrato' : 'Gerar contrato',
    ctaKey: contract ? 'contract' : 'generate_contract',
    icon: contract ? 'i-lucide-file-signature' : 'i-lucide-file-plus-2',
    blocksNextStep: !contract,
  });
};

const receiptStatusDetails = value => {
  const token = normalizedToken(value?.status || value?.key || value);

  if (
    [
      'approved',
      'paid',
      'confirmed',
      'validated',
      'complete',
      'completed',
      'guarantee_paid',
    ].includes(token)
  ) {
    return statusConfig({
      label: 'Comprovante aprovado',
      tone: 'success',
      ctaLabel: 'Ver comprovante',
      ctaKey: 'receipt',
      icon: 'i-lucide-receipt-text',
      completesStep: true,
    });
  }

  if (['rejected', 'refused', 'invalid', 'failed'].includes(token)) {
    return statusConfig({
      label: 'Comprovante rejeitado',
      tone: 'danger',
      ctaLabel: 'Reenviar comprovante',
      ctaKey: 'resend_receipt',
      icon: 'i-lucide-upload',
      blocksNextStep: true,
    });
  }

  return statusConfig({
    label: 'Comprovante pendente',
    tone: 'warning',
    ctaLabel: 'Enviar comprovante',
    ctaKey: 'send_receipt',
    icon: 'i-lucide-upload',
    blocksNextStep: true,
  });
};

const documentTitle = (item, index) =>
  firstPresent([
    item?.name,
    item?.title,
    item?.label,
    item?.document_type,
    item?.documentType,
    item?.kind,
  ]) || `Documento ${index + 1}`;

const documentResponsible = item => {
  const role = normalizedToken(
    item?.responsible ||
      item?.responsible_role ||
      item?.responsibleRole ||
      item?.party_role ||
      item?.partyRole
  );

  if (['owner', 'landlord', 'supply', 'proprietario'].includes(role)) {
    return 'Proprietário';
  }
  if (['tenant', 'demand', 'buyer', 'interessado', 'cliente'].includes(role)) {
    return 'Interessado';
  }
  if (['agent', 'broker', 'corretor'].includes(role)) return 'Corretor';
  return 'Interessado';
};

const signerRoleLabel = role => {
  const token = normalizedToken(role);
  if (['owner', 'seller', 'landlord', 'supply'].includes(token)) {
    return 'Proprietário';
  }
  if (['tenant', 'buyer', 'demand'].includes(token)) return 'Interessado';
  if (['agent', 'broker'].includes(token)) return 'Corretor';
  return role || 'Signatário';
};

const signerStatusDetails = signer => {
  const token = normalizedToken(signer?.status);

  if (['signed', 'completed', 'complete'].includes(token)) {
    return statusConfig({
      label: 'Assinatura concluída',
      tone: 'success',
      ctaLabel: 'Ver assinatura',
      ctaKey: 'signature',
      icon: 'i-lucide-file-check-2',
      completesStep: true,
    });
  }

  if (['refused', 'rejected', 'failed'].includes(token)) {
    return statusConfig({
      label: 'Assinatura recusada',
      tone: 'danger',
      ctaLabel: 'Reenviar assinatura',
      ctaKey: 'signature',
      icon: 'i-lucide-refresh-cw',
      blocksNextStep: true,
    });
  }

  return statusConfig({
    label: 'Pendente de assinatura',
    tone: 'warning',
    ctaLabel: 'Abrir assinatura',
    ctaKey: 'signature',
    icon: 'i-lucide-file-signature',
    blocksNextStep: true,
  });
};

const documentationPayload = offer =>
  typeof offer?.documentation_status === 'object'
    ? offer.documentation_status
    : {};

const documentationItemsForOffer = offer =>
  [
    offer?.documents,
    offer?.required_documents,
    offer?.requiredDocuments,
    offer?.document_artifacts,
    offer?.documentArtifacts,
    documentationPayload(offer)?.documents,
    documentationPayload(offer)?.items,
    offer?.metadata?.documents,
    offer?.metadata?.required_documents,
  ].flatMap(asPanelItems);

const receiptItemsForOffer = (offer, contract) => {
  const nativeReceipts = [
    offer?.receipts,
    offer?.payment_receipts,
    offer?.paymentReceipts,
    offer?.comprovantes,
    contract?.receipts,
    contract?.payment_receipts,
    offer?.metadata?.receipts,
    offer?.metadata?.payment_receipts,
    offer?.metadata?.proofs,
  ].flatMap(asPanelItems);

  if (nativeReceipts.length) return nativeReceipts;

  const status = normalizedToken(rentalOfferStatus(offer));
  if (
    [
      'guarantee_demand_payment_pending',
      'advance_payment_demand_pending',
      'advance_payment_demand_processing',
      'advance_payment_demand_created',
    ].includes(status)
  ) {
    return [{ kind: 'Comprovante de pagamento', status: 'pending' }];
  }

  if (status === 'guarantee_paid') {
    return [{ kind: 'Comprovante de pagamento', status: 'paid' }];
  }

  return [];
};

const kycVerificationForOffer = offer =>
  offer?.kyc_verification ||
  asArray(offer?.kyc_verifications)[0] ||
  offer?.kyc_status ||
  documentationPayload(offer)?.kyc ||
  documentationPayload(offer)?.kyc_status ||
  null;

const documentationAggregateStatus = offer =>
  firstPresent([
    documentationPayload(offer)?.status,
    documentationPayload(offer)?.key,
    offer?.documentation_status,
    rentalOfferStatus(offer),
  ]);

const documentationStagesForOffer = offer => {
  const items = documentationItemsForOffer(offer);

  if (items.length) {
    return items.map((item, index) =>
      stageFromStatus({
        key: `document-${item.id || item.key || index}`,
        title: documentTitle(item, index),
        responsible: documentResponsible(item),
        deadline: documentDeadlineValue(item),
        status: documentStatusDetails(item),
      })
    );
  }

  return [
    stageFromStatus({
      key: 'documentation-summary',
      title: 'Documentação',
      responsible: 'Interessado',
      deadline: firstPresent([
        documentationPayload(offer)?.deadline_at,
        documentationPayload(offer)?.due_at,
        offer?.metadata?.documentation_deadline_at,
        offer?.metadata?.docs_due_at,
      ]),
      status: documentStatusDetails(documentationAggregateStatus(offer)),
    }),
  ];
};

const kycStagesForOffer = offer => {
  const verification = kycVerificationForOffer(offer);
  return [
    stageFromStatus({
      key: 'kyc-verification',
      title: 'KYC do interessado',
      responsible: 'Interessado',
      deadline: firstPresent([
        verification?.deadline_at,
        verification?.due_at,
        verification?.expires_at,
        offer?.metadata?.kyc_deadline_at,
      ]),
      status: kycStatusDetails(verification || offer?.kyc_status),
    }),
  ];
};

const contractSignerStages = (contract, offer) =>
  asArray(contract?.signers).map((signer, index) =>
    stageFromStatus({
      key: `signature-${signer.id || signer.token || index}`,
      title: `Assinatura ${signerRoleLabel(
        signer.role || signer.qualification
      )}`,
      responsible: signerRoleLabel(signer.role || signer.qualification),
      deadline: firstPresent([
        signer.deadline_at,
        signer.deadline,
        signer.signature_deadline_at,
        signer.signature_deadline_date,
        contractDeadlineValue(offer, contract),
      ]),
      status: signerStatusDetails(signer),
    })
  );

const contractStagesForOffer = offer => {
  const contract = contractForOffer(offer);
  const contractStatus = contractStatusDetails(offer, contract);
  const stages = [
    stageFromStatus({
      key: 'contract-summary',
      title: contract ? 'Contrato / CCV' : 'Gerar contrato',
      responsible: 'Lais',
      deadline: contractDeadlineValue(offer, contract),
      status: contractStatus,
    }),
    ...contractSignerStages(contract, offer),
  ];

  receiptItemsForOffer(offer, contract).forEach((receipt, index) => {
    stages.push(
      stageFromStatus({
        key: `receipt-${receipt.id || receipt.key || index}`,
        title:
          firstPresent([
            receipt.name,
            receipt.title,
            receipt.kind,
            receipt.document_type,
          ]) || 'Comprovante',
        responsible: documentResponsible(receipt),
        deadline: documentDeadlineValue(receipt),
        status: receiptStatusDetails(receipt),
      })
    );
  });

  return stages;
};

const offerStagesForOffer = offer => {
  const decisionState = offerDecisionState(offer);
  const status = negotiationStatusForOffer(offer, decisionState);
  const step = currentRentProgressStep(offer);

  return [
    stageFromStatus({
      key: step?.key || 'offer-summary',
      title:
        firstPresent([step?.label, step?.title]) ||
        (status.label.includes('Contraproposta') ? 'Contraproposta' : 'Oferta'),
      responsible: roleForNegotiationStep(step, decisionState),
      deadline: offerDeadlineValue(offer),
      status,
    }),
  ];
};

const stageSummaryForCard = stages => {
  const blocking = stages.find(stage => stage.blocksNextStep);
  if (blocking) {
    return {
      label: blocking.statusLabel,
      tone: blocking.statusTone,
    };
  }

  const incomplete = stages.find(stage => !stage.completesStep);
  if (incomplete) {
    return {
      label: incomplete.statusLabel,
      tone: incomplete.statusTone,
    };
  }

  return {
    label: 'Fluxo liberado',
    tone: 'success',
  };
};

const gateForSection = (sectionKey, stages) => {
  const hasBlocker = stages.some(stage => stage.blocksNextStep);
  const allComplete =
    stages.length && stages.every(stage => stage.completesStep);

  if (sectionKey === 'documentation') {
    return {
      label: hasBlocker
        ? 'Documentação bloqueia contrato'
        : 'Documentação liberada',
      tone: hasBlocker ? 'warning' : 'success',
    };
  }

  if (sectionKey === 'kyc') {
    return {
      label: hasBlocker ? 'KYC bloqueia contrato' : 'KYC liberado',
      tone: hasBlocker ? 'warning' : 'success',
    };
  }

  if (sectionKey === 'contracts') {
    return {
      label: allComplete && !hasBlocker ? 'Fluxo concluído' : 'Contrato ativo',
      tone: allComplete && !hasBlocker ? 'success' : 'warning',
    };
  }

  return null;
};

const panelStagesForOffer = (offer, sectionKey) => {
  if (sectionKey === 'documentation') return documentationStagesForOffer(offer);
  if (sectionKey === 'kyc') return kycStagesForOffer(offer);
  if (sectionKey === 'contracts') return contractStagesForOffer(offer);
  return offerStagesForOffer(offer);
};

const offerBelongsToPanelSection = (offer, sectionKey) => {
  if (sectionKey === 'documentation') {
    return hasRentProgressDocumentationPhase(offer);
  }

  if (sectionKey === 'contracts') {
    return hasRentProgressContractPhase(offer);
  }

  if (sectionKey === 'kyc') {
    return Boolean(
      kycVerificationForOffer(offer) || offer?.status === 'accepted'
    );
  }

  return true;
};

export const rentProgressPanelCard = (offer, sectionKey = 'offers') => {
  const normalizedSection = PANEL_TAB_KEYS.has(sectionKey)
    ? sectionKey
    : 'offers';
  const stages = panelStagesForOffer(offer, normalizedSection);
  const summary = stageSummaryForCard(stages);
  const primaryStage = stages.find(stage => stage.blocksNextStep) || stages[0];

  return {
    key: `${normalizedSection}-${offer?.id || panelReferenceForOffer(offer)}`,
    sectionKey: normalizedSection,
    offer,
    title: panelTitleForOffer(offer),
    subtitle: panelSubtitleForOffer(offer),
    reference: panelReferenceForOffer(offer),
    amountLabel: formatPanelCurrency(offer?.offer_amount),
    statusLabel: summary.label,
    statusTone: summary.tone,
    responsible: primaryStage?.responsible || 'Lais',
    deadline: primaryStage?.deadline || 'Sem prazo definido',
    cta: primaryStage?.cta || offerCardPrimaryAction(offer, normalizedSection),
    gate: gateForSection(normalizedSection, stages),
    stages,
    timeline:
      normalizedSection === 'offers'
        ? rentProgressNegotiationSummaries(offer)
        : [],
  };
};

export const rentProgressPanelSections = offers =>
  RENT_PROGRESS_PANEL_TABS.map(tab => ({
    ...tab,
    cards: asArray(offers)
      .filter(offer => offerBelongsToPanelSection(offer, tab.key))
      .map(offer => rentProgressPanelCard(offer, tab.key)),
  }));

// `now` is injectable so the CTA a card renders is a pure function of the
// payload plus a caller-controlled clock. Reading the wall clock here made card
// CTAs untestable and let pinned fixtures rot the moment their expiry passed.
export const rentProgressCardActions = (offer, phase, { now } = {}) => {
  const normalizedPhase = normalizeRentProgressPhase(phase);
  const actions = [];
  const decisionState = offerDecisionState(offer, { now });
  const primaryAction = offerCardPrimaryAction(offer, normalizedPhase, { now });

  actions.push(primaryAction);

  if (offer?.conversation_id || offer?.conversation?.id) {
    actions.push({
      key: 'chat_client',
      label: 'Chat',
      icon: 'i-lucide-message-circle',
      tone: 'secondary',
    });
  }

  const ownerContact =
    offer?.imovel?.owner_contact ||
    offer?.owner_contact ||
    offer?.metadata?.owner_contact ||
    {};
  if (
    ownerContact.phone_number ||
    ownerContact.phoneNumber ||
    offer?.metadata?.owner_phone
  ) {
    actions.push({
      key: 'owner_contact',
      label: 'Proprietário',
      icon: 'i-lucide-house',
      tone: 'secondary',
    });
  }

  if (
    decisionState.accepted &&
    normalizedPhase === RENT_PROGRESS_PHASES.DOCUMENTATION &&
    primaryAction.key !== 'kyc'
  ) {
    actions.push({
      key: 'kyc',
      label: 'KYC',
      icon: 'i-lucide-badge-check',
      tone: 'secondary',
    });
  }

  if (
    decisionState.accepted &&
    normalizedPhase === RENT_PROGRESS_PHASES.CONTRACT &&
    primaryAction.key !== 'contract'
  ) {
    actions.push({
      key: 'contract',
      label: 'Contrato',
      icon: 'i-lucide-file-check-2',
      tone: 'secondary',
    });
  }

  return actions;
};

export const rentProgressPhaseEmptyState = ({
  phase,
  hasFilters = false,
  isError = false,
  limitDays = RENT_PROGRESS_LIMIT_DAYS,
} = {}) => {
  if (isError) {
    return {
      title: 'Ocorreu um erro',
      description: 'Tente de novo em alguns instantes.',
      buttonLabel: 'Tentar de novo',
    };
  }

  const copy = PHASE_EMPTY_COPY[normalizeRentProgressPhase(phase)];
  if (hasFilters) {
    return {
      title: 'Nenhum resultado encontrado',
      description: copy.filterDescription,
    };
  }

  return {
    title: copy.title(limitDays),
    description: copy.description,
  };
};
